import update from "immutability-helper";
import moment from "moment";
import React, { PropsWithChildren, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { setActivityList } from "../actions/actions";
import {
  createActivity,
  updateActivitybyId,
} from "../services/activityService";
import InputMaps from "./InputMaps";
import ErrorText from "./errorText";
import Inputs from "./inputs";
import PopupComponent from "./PopupComponent";
import styled from "styled-components";
import { Logger } from '../utils/logger';
import { Settings, Plus, X } from 'lucide-react';
import { updateUserPreferences } from '../services/userService';

interface Props {
  leadIds: Array<{ id: string }>;
  updateModalValue: any;
  action: string;
  formName: string;
  handleActivityFormSubmit: (data: any) => void;
}

const CreateActivityForm: React.FC<PropsWithChildren<Props>> = ({
  leadIds,
  updateModalValue,
  action,
  formName,
  handleActivityFormSubmit,
}) => {
  const [activity, setActivity] = useState<any>({
    type: "",
    performedAt: new Date(),
    createdTimestamp: new Date().getTime(), // Initialize with current timestamp
    notes: "",
    leadIds: [leadIds[0]?.id],
  });
  
  const [errors, setErrors] = useState({
    type: "",
    performedAt: "",
    createdTimestamp: "",
    notes: "",
  });
  const [activityType, setActivityType] = useState<any>([]);
  const [showformData, setShowformData] = useState<boolean>(false);
  const [formData, setFormData] = useState<any>([]);
  const [formTime, setFormTime] = useState<string>("");
  const [addressName, setAddressName] = useState({});
  const [coordinates, setCoordinates] = useState({ lat: 0, lng: 0 });

  const DEFAULT_NOTE_CHIPS = [
    "Interested", "Not Interested", "Call Back Later", "Wrong Number",
    "No Response", "Meeting Scheduled", "Follow Up Required", "Deal Closed"
  ];

  const [noteChips, setNoteChips] = useState<string[]>(DEFAULT_NOTE_CHIPS);
  const [showChipEditor, setShowChipEditor] = useState(false);
  const [editableChips, setEditableChips] = useState<string[]>([]);
  const [newChipText, setNewChipText] = useState("");

  const dispatch = useDispatch();
  const StoreData = useSelector((state: any) => state?.rootReducers);

  useEffect(() => {
    let userPreferences = JSON.parse(
      localStorage.getItem("user_preferences") || ""
    );
    setActivityType(userPreferences?.activityType);
    // Load saved note chips from userPreferredPreference
    const savedChips = userPreferences?.userPreferredPreference?.activityNoteChips;
    if (savedChips && Array.isArray(savedChips)) {
      setNoteChips(savedChips);
    }
  }, []);

  useEffect(() => {
    if (formName === "Activity" && activityType?.length > 0) {
      if (Object.values(updateModalValue).length > 0) {
        let someDate = new Date(updateModalValue.performedAt);
        let date = someDate.setDate(someDate.getDate());
        let defaultValue = new Date(date)?.toISOString()?.split("T")[0];
        setActivity({
          type: updateModalValue.type,
          // performedAt: new Date(defaultValue),
          performedAt: updateModalValue.performedAt,
          createdTimestamp: updateModalValue.performedAt,
          notes: updateModalValue.notes ? updateModalValue.notes : "",
          leadIds: [leadIds[0]?.id],
        });
        let filterData = activityType.filter(
          (x: any) => x.value === updateModalValue.type
        );
        if (filterData?.length > 0) {
          if (filterData[0]?.customFields) {
            let tempArrya = filterData[0]?.customFields;
            let updateData = updateModalValue.customFields || [];
            tempArrya = tempArrya.map((item: any) => {
              const item2 = updateData.find(
                (i2: any) => i2.label === item.name
              );
              return item2 ? { ...item, Values: item2.value, value: item2.value } : { ...item, Values: "" };
            });

            setFormData(tempArrya);
          }
          setShowformData(true);
        } else {
          setFormData([]);
          setShowformData(false);
        }
      } else {
        resetFormvalue();
      }
    }
  }, [updateModalValue, activityType]);

  let schema = yup.object().shape({
    type: yup.string().required("Activity Type is required"),
    performedAt: yup.date().required("Activity Date is required"),
    createdTimestamp: yup.string().required("Activity Time is required"),
    notes: yup.string(),
  });

  const resetFormvalue = () => {
    setActivity({
      type: "",
      performedAt: new Date(),
      createdTimestamp: new Date().getTime(), // Update here too
      notes: "",
      leadIds: [leadIds[0]?.id],
    });
    setFormTime(moment(new Date()).format("HH:mm")); // Also initialize formTime with current time
    setFormData([]);
    setShowformData(false);
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.currentTarget;
    let date_a = moment(`${value}`, "hh:mm").format("YYYY-MM-DD H:mm:ss");
    if (name === "createdTimestamp") {
      setActivity({
        ...activity,
        [name]: new Date(date_a).getTime(),
      });
      setFormTime(value);
    } else {
      setActivity({
        ...activity,
        [name]: value,
      });
    }
    setErrors({
      ...errors,
      [name]: null,
    });
  };

  const handleSelectOnchange = (e: any, check: string) => {
    const { name, value } = e.currentTarget;
    if (check === "type") {
      setActivity({
        ...activity,
        [name]: value,
      });
      let filterData = activityType.filter((x: any) => x.value === value);
      if (filterData?.length > 0) {
        setFormData([]);
        if (filterData[0]?.customFields) {
          let tempArrya = filterData[0]?.customFields;
          tempArrya.forEach((item: any) => {
            item.Values = "";
          });
          setFormData(tempArrya);
        }
        setShowformData(true);
      } else {
        setFormData([]);
        setShowformData(false);
      }
    }

    setErrors({
      ...errors,
      [name]: null,
    });
  };

  const handleCustomChange = (
    i: number,
    e:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = e.currentTarget;
    let tempArray = [...formData];

    let obj = tempArray[i];
    obj.Values = value;
    obj.value = value;
    tempArray[i] = obj;
    setFormData([...formData]);

    setActivity({
      ...activity,
      [name]: value,
    });
  };

  const handleMapChange = (i: number, value: string, coords: any) => {
    const user = {
      address: value,
      coords: {
        latitude: coords.lat,
        longitude: coords.lng,
      },
    };
    let tempArray = [...formData];

    let obj = tempArray[i];
    obj.Values = user;
    tempArray[i] = obj;
    setFormData([...formData]);

    setAddressName(user);
    setCoordinates(coords);
  };

  // Quick-fill chip click — append to notes
  const handleChipClick = (chipText: string) => {
    const currentNotes = activity.notes || "";
    const separator = currentNotes.trim() ? " - " : "";
    setActivity({ ...activity, notes: currentNotes + separator + chipText });
  };

  // Open chip editor popup
  const openChipEditor = () => {
    setEditableChips([...noteChips]);
    setNewChipText("");
    setShowChipEditor(true);
  };

  // Add a new chip in editor
  const addNewChip = () => {
    const trimmed = newChipText.trim();
    if (trimmed && !editableChips.includes(trimmed)) {
      setEditableChips([...editableChips, trimmed]);
      setNewChipText("");
    }
  };

  // Remove a chip in editor
  const removeEditableChip = (index: number) => {
    setEditableChips(editableChips.filter((_, i) => i !== index));
  };

  // Save chips to API + localStorage
  const saveChips = async () => {
    try {
      // Read existing userPreferredPreference and merge activityNoteChips into it
      const fullPrefs = JSON.parse(localStorage.getItem("user_preferences") || "{}");
      const existingPreferred = fullPrefs.userPreferredPreference || {};
      const updatedPreferred = { ...existingPreferred, activityNoteChips: editableChips };

      // Only send userPreferredPreference key to API
      await updateUserPreferences({ userPreferredPreference: updatedPreferred });

      // Update localStorage too
      fullPrefs.userPreferredPreference = updatedPreferred;
      localStorage.setItem("user_preferences", JSON.stringify(fullPrefs));

      setNoteChips(editableChips);
      setShowChipEditor(false);
      toast.success("Quick notes saved");
    } catch (err) {
      Logger.log("Error saving note chips:", err);
      toast.error("Failed to save quick notes");
    }
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const isFormValid = await schema.isValid(activity, {
      abortEarly: false, // Prevent aborting validation after first error
    });
    if (isFormValid) {
      try {
        let Objdata: any = {};
        let time_stamp = 0;
        if (action === "Add") {
          time_stamp = activity.createdTimestamp;
        } else {
          if (new Date(activity.createdTimestamp).getDate()) {
            time_stamp = new Date(activity.createdTimestamp).getTime();
          } else {
            time_stamp = activity.createdTimestamp;
          }
        }
        let someDate = new Date(activity.performedAt);
        let date = someDate.setDate(someDate.getDate());
        let defaultValue = new Date(date)?.toISOString()?.split("T")[0];

        let Form_time: any = "";
        if (formTime) {
          Form_time = formTime;
        } else {
          Form_time = new Date();
        }
        let tempTime = moment(Form_time, "hh:mm:ss").format("hh:mm:ss");
        let UtcDate = moment(`${defaultValue}T${tempTime}`).utc().toISOString();

        let editObjData: any = {
          type: activity.type,
          performedAt: UtcDate,
          createdTimestamp: time_stamp,
          ...(activity.notes?.length > 0 && {
            notes: activity.notes,
          }),
        };
        let activityObj: any = {
          leadIds: [leadIds[0]?.id],
          type: activity.type,
          performedAt: UtcDate,
          createdTimestamp: time_stamp,
          ...(activity.notes?.length > 0 && {
            notes: activity.notes,
          }),
        };
        let customFields: any = [];
        const filteredFormData = formData.filter((item: any) => item.type !== "file");
        for (let i = 0; i < filteredFormData.length; i++) {
          let tempObj = {
            field: filteredFormData[i].value,
            label: filteredFormData[i].name,
            value: filteredFormData[i].Values,
          };

          customFields.push(tempObj);
        }

        if (showformData && customFields.length > 0) {
          if (action === "Add") {
            Objdata = {
              ...activityObj,
              customFields,
            };
          } else {
            Objdata = {
              ...editObjData,
              customFields,
            };
          }
        } else {
          if (action === "Add") {
            Objdata = activityObj;
          } else {
            Objdata = editObjData;
          }
        }
        let response: any = "";
        if (action === "Add") {
          response = await createActivity(Objdata);
        } else {
          response = await updateActivitybyId(updateModalValue._id, Objdata);
        }
        if (response && response.status) {
          const responseData = response?.data;
          const tempArray = [...StoreData?.activity?.activities];
          if (action === "Add") {
            tempArray?.unshift(responseData?.data);
          } else {
            const tempData = tempArray.findIndex(
              (x) => x._id == responseData.data._id
            );
            tempArray.splice(tempData, 1, responseData.data);
          }
          dispatch(setActivityList(tempArray));
          handleActivityFormSubmit(response?.data?.lead);
          toast.success(responseData?.message);
          resetFormvalue();
        }
      } catch (err) {
        Logger.log("error while creating activity!");
        // toast.error("error while creating activity!");
      }
    } else {
      schema.validate(activity, { abortEarly: false }).catch((err) => {
        const errors = err.inner.reduce(
          (acc: any, error: { path: string; message: string }) => {
            return {
              ...acc,
              [error.path]: error.message,
            };
          },
          {}
        );
        setErrors((prevErrors) =>
          update(prevErrors, {
            $set: errors,
          })
        );
      });
    }
  };

  return (
    <Root>
      <div>
        {action === "Add" && (
          <div className="form-group">
            <label className="form-label">Activity Type </label>
            <select
              value={activity?.type}
              name="type"
              className="form-select"
              onChange={(e: any) => handleSelectOnchange(e, "type")}
            >
              <option value="">Select type</option>
              {activityType.map((type: any, ix: number) => (
                <option value={type.value} key={ix}>
                  {type.name}
                </option>
              ))}
            </select>
            {errors.type && <ErrorText message="Activity type required" />}
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Activity Date</label>
          <input
  type="date"
  name="performedAt"
  className="form-control"
  value={moment(activity.performedAt).format("YYYY-MM-DD")}
  max={moment().format("YYYY-MM-DD")} // Changed min to max to disable future dates
  placeholder="Enter Date"
  onChange={(e) => handleChange(e)}
/>
          {errors.performedAt && <ErrorText message="Activity date required" />}
        </div>
        <div className="form-group">
          <label className="form-label">Activity Time</label>
          <input
            type="time"
            name="createdTimestamp"
            value={moment(new Date(activity.createdTimestamp), "hh:mm").format(
              "HH:mm"
            )}
            className="form-control"
            placeholder="Enter Time"
            onChange={(e) => handleChange(e)}
          />
          {errors.createdTimestamp && (
            <ErrorText message="Activity time required" />
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Note</label>
          <textarea
            onChange={(e) => handleChange(e)}
            name="notes"
            className="form-control note_area"
            style={{ height: "120px" }}
            placeholder="Enter Note"
            value={activity.notes}
          />
          {errors.notes && <ErrorText message="Activity note required" />}
          {/* Quick-fill note chips */}
          <div className="note-chips-container">
            <div className="note-chips-row">
              {noteChips.map((chip, i) => (
                <button
                  key={i}
                  type="button"
                  className="note-chip"
                  onClick={() => handleChipClick(chip)}
                >
                  {chip}
                </button>
              ))}
              <button
                type="button"
                className="note-chip-settings"
                onClick={openChipEditor}
                title="Edit quick notes"
              >
                <Settings size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* Chip Editor Popup */}
        <PopupComponent
          isOpen={showChipEditor}
          onClose={() => setShowChipEditor(false)}
          title="Edit Quick Notes"
          size="sm"
          footer={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
              <button
                type="button"
                onClick={() => setShowChipEditor(false)}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db',
                  background: '#fff', cursor: 'pointer', fontFamily: '"Gilroy-Medium", sans-serif', fontSize: '13px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveChips}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  background: '#3faefd', color: '#fff', cursor: 'pointer',
                  fontFamily: '"Gilroy-Medium", sans-serif', fontSize: '13px',
                }}
              >
                Save
              </button>
            </div>
          }
        >
          <div style={{ fontFamily: '"Gilroy-Medium", sans-serif' }}>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px 0' }}>
              Click chips to remove. Add new ones below.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
              {editableChips.map((chip, i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '4px 10px', background: '#e0f2fe', color: '#0369a1',
                    borderRadius: '14px', fontSize: '12px', fontFamily: '"Gilroy-Medium", sans-serif',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onClick={() => removeEditableChip(i)}
                >
                  {chip}
                  <X size={12} style={{ opacity: 0.6 }} />
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={newChipText}
                onChange={(e) => setNewChipText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewChip(); } }}
                placeholder="Type a new quick note..."
                style={{
                  flex: 1, padding: '8px 12px', border: '1px solid #d1d5db',
                  borderRadius: '6px', fontSize: '13px', fontFamily: '"Gilroy-Medium", sans-serif',
                  outline: 'none',
                }}
              />
              <button
                type="button"
                onClick={addNewChip}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '8px 12px', background: '#3faefd', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '13px', fontFamily: '"Gilroy-Medium", sans-serif',
                }}
              >
                <Plus size={14} /> Add
              </button>
            </div>
          </div>
        </PopupComponent>

        {formData?.filter((item: any) => item.type !== "file")?.map((item: any, i: number) => {
          return (
            <div
              className="form-group hide_dt select_icon activity_form"
              key={i}
            >
              {item.type === "location" ? (
                <InputMaps
                  type={item.type}
                  name={item?.value}
                  value={item?.Values || ""}
                  onChange={(name: string, value: string, coords: any) =>
                    handleMapChange(i, value, coords)
                  }
                  className="form-control"
                  label={item.name}
                  isRequired={item.isRequired || false}
                />
              ) : (
                <Inputs
                  name={item?.value}
                  type={
                    item.type === "phone" || item.type === "amount"
                      ? "number"
                      : item.type
                  }
                  autoComplete="off"
                  className={"form-control"}
                  placeholder={`Enter ${item?.name}`}
                  value={item?.Values || ""}
                  onChange={(e: any) => handleCustomChange(i, e)}
                  label={item.name}
                  isRequired={item.isRequired || false}
                  options={
                    item.type === "selection" &&
                    (item?.options ?? item?.fieldOptions)
                  }
                  id={
                    item.type === "time" || item.type === "date"
                      ? "lead_date_time"
                      : ""
                  }
                />
              )}
            </div>
          );
        })}

        {/* Read-only extra details section (edit mode only) */}
        {action === "Edit" && updateModalValue?.customFields?.length > 0 && (
          <div className="activity-extra-details">
            <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Activity Details</label>
            <div className="extra-details-list">
              {updateModalValue.customFields.map((field: any, i: number) => (
                <div key={i} className="extra-detail-row">
                  <span className="extra-detail-label">{field.label || field.field}</span>
                  <span className="extra-detail-value">
                    {typeof field.value === 'object' && field.value !== null
                      ? field.value.address || JSON.stringify(field.value)
                      : String(field.value || '-')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="auto_form_btn">
        <button
          type="button"
          onClick={(e) => handleSubmit(e)}
          className="btn btn-primary-save"
        >
          {action} Activity
        </button>
      </div>
      <Toaster position="top-right" reverseOrder={false} />
    </Root>
  );
};

export default CreateActivityForm;
const Root = styled.section`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  font-family: "Gilroy-Medium", "Gilroy", sans-serif;

  .form-group {
    margin: 6px 0px;
  }

  .form-label {
    font-family: "Gilroy-Bold", "Gilroy", sans-serif;
    font-size: 13px;
    color: #374151;
  }

  .form-control, .form-select {
    font-family: "Gilroy-Medium", "Gilroy", sans-serif;
    font-size: 13px;
  }

  .auto_form_btn {
    position: sticky;
    margin-top: auto;
    bottom: 0px;

    button {
      font-family: "Gilroy-Bold", "Gilroy", sans-serif;
      font-size: 14px;
    }
  }

  .select_date {
    display: block;
    background: #fff;
    border-radius: 2px;
    padding: 8px;
    border: 1px solid #ced4da;
    span.rs-picker-toggle-value {
      color: #000;
    }
    &:focus-visible {
      outline: none;
      box-shadow: none;
      border: none;
    }
  }

  .note-chips-container {
    margin-top: 8px;
  }

  .note-chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }

  .note-chip {
    padding: 3px 10px;
    background: #f0f9ff;
    color: #0369a1;
    border: 1px solid #bae6fd;
    border-radius: 14px;
    font-size: 11px;
    font-family: "Gilroy-Medium", "Gilroy", sans-serif;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;

    &:hover {
      background: #e0f2fe;
      border-color: #7dd3fc;
    }
  }

  .activity-extra-details {
    margin-top: 12px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px;
  }

  .extra-details-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .extra-detail-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 6px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  .extra-detail-label {
    font-family: "Gilroy-Medium", "Gilroy", sans-serif;
    font-size: 12px;
    color: #6b7280;
    flex-shrink: 0;
    margin-right: 12px;
  }

  .extra-detail-value {
    font-family: "Gilroy-Medium", "Gilroy", sans-serif;
    font-size: 13px;
    color: #1a1a1a;
    text-align: right;
    word-break: break-word;
  }

  .note-chip-settings {
    padding: 3px 6px;
    background: none;
    border: 1px solid #d1d5db;
    border-radius: 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    color: #6b7280;
    transition: all 0.15s;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }
`;
